import { Link } from 'react-router-dom';
import Arrow from '../../components/ui/Arrow';
import BlurText from '../../components/effects/BlurText';
import { brand } from '../../content/site';
import { localizedPath, useLocale, useMessages } from '../../i18n/locale';

function Breadcrumb({ copy, locale, current }) {
  return (
    <nav className="company-breadcrumb" aria-label={copy.breadcrumbLabel}>
      <Link to={localizedPath(locale)}>{copy.home}</Link>
      <span aria-hidden="true">/</span>
      {current ? (
        <Link to={localizedPath(locale, '/company')}>{copy.name}</Link>
      ) : (
        <span>{copy.name}</span>
      )}
      {current && (
        <>
          <span aria-hidden="true">/</span>
          <span>{current}</span>
        </>
      )}
    </nav>
  );
}

function Overview({ copy, locale }) {
  const strands = Array.from({ length: 25 }, (_, index) => {
    const progress = index / 24;
    const startY = 34 + progress * 382;
    const endY = 416 - progress * 382;
    const offset = (progress - 0.5) * 142;

    return {
      index,
      path: `M 0 ${startY} C 132 ${startY - 18}, 190 ${204 + offset}, 300 225 C 410 ${246 - offset}, 468 ${endY + 18}, 600 ${endY}`,
    };
  });
  const lightPaths = strands.filter(({ index }) => index % 5 === 2);

  return (
    <main className="company-page company-page--overview">
      <section className="company-overview">
        <div className="company-overview__grid" aria-hidden="true" />
        <div className="container company-overview__inner">
          <div className="company-overview__copy">
            <Breadcrumb copy={copy} locale={locale} />
            <h1>
              {copy.overview.title.map((line) => (
                <span className="company-overview__title-line" key={line}>
                  <BlurText
                    className="company-overview__title-text"
                    text={line}
                    delay={line === copy.overview.title[0] ? 40 : 180}
                  />
                </span>
              ))}
            </h1>
            <p>{copy.overview.description.replaceAll('{brand}', brand.name)}</p>
            <a className="company-overview__cta" href="#company-paths">
              {copy.overview.explore} <Arrow />
            </a>
          </div>
          <div className="company-overview__visual" aria-hidden="true">
            <svg className="company-weave" viewBox="0 0 600 450" role="presentation">
              <defs>
                <linearGradient id="company-weave-fade" x1="0" x2="1" y1="0" y2="0">
                  <stop offset="0" stopColor="currentColor" stopOpacity="0.015" />
                  <stop offset="0.3" stopColor="currentColor" stopOpacity="0.2" />
                  <stop offset="0.5" stopColor="currentColor" stopOpacity="0.88" />
                  <stop offset="0.7" stopColor="currentColor" stopOpacity="0.2" />
                  <stop offset="1" stopColor="currentColor" stopOpacity="0.015" />
                </linearGradient>
                <radialGradient id="company-weave-bloom">
                  <stop offset="0" stopColor="currentColor" stopOpacity="0.42" />
                  <stop offset="0.2" stopColor="currentColor" stopOpacity="0.18" />
                  <stop offset="1" stopColor="currentColor" stopOpacity="0" />
                </radialGradient>
                <filter id="company-weave-blur" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="5" />
                </filter>
                <filter
                  id="company-weave-particle-glow"
                  x="-300%"
                  y="-300%"
                  width="700%"
                  height="700%"
                >
                  <feGaussianBlur stdDeviation="2.5" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              <circle className="company-weave__bloom" cx="300" cy="225" r="135" />
              <ellipse
                className="company-weave__ring company-weave__ring--outer"
                cx="300"
                cy="225"
                rx="106"
                ry="35"
              />
              <ellipse
                className="company-weave__ring company-weave__ring--inner"
                cx="300"
                cy="225"
                rx="58"
                ry="18"
              />
              {strands.map(({ index, path }) => (
                <g className="company-weave__strand" key={index}>
                  <path className="company-weave__glow" d={path} />
                  <path
                    className="company-weave__line"
                    d={path}
                    style={{ '--strand-index': index }}
                  />
                </g>
              ))}
              {lightPaths.map(({ index, path }) => (
                <circle className="company-weave__particle" key={`light-${index}`} r="2.5">
                  <animateMotion
                    begin={`${index * -0.32}s`}
                    dur={`${6.8 + (index % 3) * 0.9}s`}
                    path={path}
                    repeatCount="indefinite"
                  />
                </circle>
              ))}
              <circle className="company-weave__focus-halo" cx="300" cy="225" r="15" />
              <circle className="company-weave__focus" cx="300" cy="225" r="3" />
            </svg>
          </div>
        </div>
      </section>

      <section className="company-paths container" id="company-paths">
        <div className="company-paths__heading">
          <span className="company-eyebrow">{copy.overview.pathsEyebrow}</span>
          <h2>{copy.overview.pathsTitle}</h2>
        </div>
        <div className="company-path-list">
          {copy.paths.map((path) => (
            <Link className="company-path" key={path.number} to={localizedPath(locale, path.href)}>
              <span className="company-path__number">{path.number}</span>
              <span className="company-path__copy">
                <strong>{path.title}</strong>
                <small>{path.description}</small>
              </span>
              <span className="company-path__arrow" aria-hidden="true">
                <Arrow up />
              </span>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}

function StoryPage({ copy, locale }) {
  return (
    <main className="company-page company-page--story">
      <section className="company-subhero">
        <div className="company-overview__grid" aria-hidden="true" />
        <div className="container company-subhero__inner">
          <Breadcrumb copy={copy} locale={locale} current={copy.story.name} />
          <span className="company-eyebrow">{copy.story.eyebrow}</span>
          <h1>
            {copy.story.title.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </h1>
          <p>{copy.story.description.replaceAll('{brand}', brand.name)}</p>
        </div>
      </section>

      <section className="company-story container" aria-label={copy.story.sequenceLabel}>
        <div className="company-story__stage">
          <div className="company-story__stage-top">
            <span>{copy.story.sequenceLabel}</span>
            <span>
              00 <i>—</i> 01
            </span>
          </div>
          <svg viewBox="0 0 1200 330" preserveAspectRatio="none" aria-hidden="true">
            <path
              className="company-story__track"
              d="M56 238 C 278 238, 291 88, 493 88 S 714 238, 900 238 S 1034 88, 1144 88"
            />
            <path
              className="company-story__track-glow"
              d="M56 238 C 278 238, 291 88, 493 88 S 714 238, 900 238 S 1034 88, 1144 88"
            />
            <circle cx="56" cy="238" r="7" />
            <circle cx="493" cy="88" r="5" />
            <circle cx="900" cy="238" r="5" />
            <circle cx="1144" cy="88" r="8" />
          </svg>
          <div className="company-story__endpoints">
            <span>0</span>
            <span>1</span>
          </div>
        </div>
        <div className="company-story__chapters">
          {copy.story.chapters.map((chapter) => (
            <article className="company-story__chapter" key={chapter.number}>
              <span>{chapter.number}</span>
              <h2>{chapter.title}</h2>
              <p>{chapter.description}</p>
            </article>
          ))}
        </div>
        <div className="company-story__note">
          <span aria-hidden="true">✳</span>
          <p>{copy.story.note}</p>
        </div>
      </section>
    </main>
  );
}

function CommunityPage({ copy, locale }) {
  return (
    <main className="company-page company-page--community">
      <section className="company-subhero company-subhero--community">
        <div className="company-overview__grid" aria-hidden="true" />
        <div className="container company-subhero__inner">
          <Breadcrumb copy={copy} locale={locale} current={copy.community.name} />
          <span className="company-eyebrow">{copy.community.eyebrow}</span>
          <h1>
            {copy.community.title.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </h1>
          <p>{copy.community.description}</p>
          <Link className="company-primary-link" to={localizedPath(locale, '/open-source')}>
            {copy.community.action} <Arrow />
          </Link>
        </div>
      </section>
      <section className="company-contribute container">
        <div className="company-paths__heading">
          <span className="company-eyebrow">{copy.community.stepsEyebrow}</span>
          <h2>{copy.community.stepsTitle}</h2>
        </div>
        <div className="company-contribute__list">
          {copy.community.steps.map((step) => (
            <article className="company-contribute__step" key={step.number}>
              <span>{step.number}</span>
              <div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
              <span aria-hidden="true">↗</span>
            </article>
          ))}
        </div>
      </section>
      <div className="container company-back-link">
        <Link to={localizedPath(locale, '/company')}>
          {copy.backToAbout} <Arrow up />
        </Link>
      </div>
    </main>
  );
}

export default function CompanyPage({ section }) {
  const locale = useLocale();
  const copy = useMessages().companyPage;

  if (section === 'story') return <StoryPage copy={copy} locale={locale} />;
  if (section === 'community') return <CommunityPage copy={copy} locale={locale} />;
  return <Overview copy={copy} locale={locale} />;
}
